/**
 * @file ShopIdentity.tsx
 * @description Шапка боковой панели кабинета магазина: «магазин на виду» и
 * переключатель магазинов и кабинетов (phase19.31, SC19, SC21;
 * uiux-design §2).
 *
 * Монограмма и название магазина; под названием — «Склад» на экране
 * кладовщика. Если кабинетов у пользователя два и больше, шапка — кнопка:
 *  - с 1024px — выпадающий список под шапкой;
 *  - до 1024px (меню в шторке) — полноэкранное окно «Выбор магазина».
 * Выбор магазина ведёт в ТОТ ЖЕ раздел, если на него есть права, иначе на
 * главную (`sameSectionPath`); в конкретную запись — никогда. Переход
 * меняет только кабинет ЭТОЙ вкладки: адрес сам выбирает кабинет
 * (`RequireContextType`), сессия и соседние вкладки не меняются (SC21).
 * Во время входа под сотрудником переключателя нет — только его кабинет.
 */

import { ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { landingPathFor } from "../../auth/format";
import { useAuth } from "../../auth/hooks";
import type { AccountContext, ShopRef } from "../../auth/types";
import { BELOW_LG_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import { findSectionItem, sameSectionPath, shopRelativePath, type VisibleShopNav } from "../../lib/shop-nav";
import { useAppShell } from "../ui/AppShell";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ShopMonogram } from "./ShopMonogram";
import { ShopSwitcherList } from "./ShopSwitcherList";

interface Props {
  shop: ShopRef;
  nav: VisibleShopNav;
}

const TRIGGER_CLASS =
  "flex min-h-12 w-full items-center gap-3 rounded-md px-2 py-1 text-left outline-none transition-colors hover:bg-sidebar-hover focus-visible:ring-2 focus-visible:ring-sidebar-ring";

export function ShopIdentity({ shop, nav }: Props) {
  const { me } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { closeSidebar } = useAppShell();
  const belowLg = useMediaQuery(BELOW_LG_QUERY);
  const [open, setOpen] = useState(false);

  const switchable = !!me && !me.impersonation && me.contexts.length > 1;
  const caption = nav.mode === "warehouse" ? "Склад" : null;
  const face = <IdentityFace shop={shop} caption={caption} switchable={switchable} />;
  if (!switchable || !me) return <div className="flex min-h-12 items-center gap-3 px-2">{face}</div>;

  function onPick(ctx: AccountContext) {
    setOpen(false);
    closeSidebar();
    if (ctx.id === me?.activeContext?.id) return;
    const section = findSectionItem(nav, shopRelativePath(pathname, shop.slug));
    navigate(ctx.contextType === "SHOP" ? sameSectionPath(section, ctx) : landingPathFor(ctx));
  }

  const list = <ShopSwitcherList contexts={me.contexts} currentId={me.activeContext?.id ?? null} onPick={onPick} />;
  const label = `Магазин «${shop.displayName}». Сменить магазин или кабинет`;

  if (belowLg) {
    return (
      <>
        <button type="button" className={TRIGGER_CLASS} aria-label={label} onClick={() => setOpen(true)}>
          {face}
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent fullScreen="lg" className="gap-3">
            <DialogTitle>Выбор магазина</DialogTitle>
            {list}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={(props) => (
          <button {...props} type="button" className={TRIGGER_CLASS} aria-label={label}>
            {face}
          </button>
        )}
      />
      <PopoverContent align="start" className="w-80 max-h-[70vh] overflow-y-auto p-2">
        {list}
      </PopoverContent>
    </Popover>
  );
}

/** Монограмма, название и подпись — общая «внешность» шапки. */
function IdentityFace({ shop, caption, switchable }: { shop: ShopRef; caption: string | null; switchable: boolean }) {
  return (
    <>
      <ShopMonogram shopId={shop.id} name={shop.displayName} />
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-sm font-semibold text-sidebar-strong" title={shop.displayName}>
          {shop.displayName}
        </span>
        {caption ? <span className="truncate text-xs text-sidebar-muted">{caption}</span> : null}
      </span>
      {switchable ? <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-sidebar-muted" /> : null}
    </>
  );
}
